import { CSSProperties, useContext } from 'react';

import {
  useAddMediaPlanRowMutation,
  useUpdateMediaPlanRowOldMutation,
} from '../../../api/mediaPlansApiSlice.js';
import { BUILDER_ROW_TYPES } from '../../../constants/campaignBuilder.js';
import { TableContentContext } from '../../../context/TableContentContext.jsx';
import { useAddMediaPlanRow } from '../../../utils/campaigns/myCampaigns.js';
import { BuilderTableRow, DraggableItemType } from '../types.js';
import TableRow from './BuilderTable/BuilderTableBody/TableRow.jsx';

const REORDER = 'REORDER';

type Props = {
  style: CSSProperties;
  index: number;
};

const RenderRow = ({ style, index }: Props) => {
  const { tableContent } = useContext(TableContentContext) || {};

  const {
    rows = [],
    prepareRow,
    setRowState,
    toggleRowExpanded,
    startMeasurePerformance = () => {},
  } = tableContent || {};

  const [addMediaPlanRow] = useAddMediaPlanRowMutation();
  const [updateMediaPlanRowMutation] = useUpdateMediaPlanRowOldMutation();

  const { onSuccess } = useAddMediaPlanRow();

  const row = rows[index];

  if (!row) {
    return null;
  }

  prepareRow?.(row);

  const isCampaignType = row?.original.type === BUILDER_ROW_TYPES.TILE;
  const isArchivedVersion = row?.original.isArchivedVersion;

  const parentRow = rows.find(
    (originalRow) => originalRow.id === row?.original.parent,
  );

  const isParentHighlighted = parentRow?.state?.isNewAdded && isCampaignType;

  const handleReorder = (item: DraggableItemType, order: number) => {
    if (item.original && item.original.order !== order) {
      updateMediaPlanRowMutation({
        rowId: item.id,
        order: item.original.order > order ? order : order + 1,
      });
    }
  };

  const handleTile = async (
    item: DraggableItemType,
    droppedRow: BuilderTableRow,
  ) => {
    const parent =
      droppedRow.original.type === BUILDER_ROW_TYPES.TILE
        ? droppedRow.original.parent
        : droppedRow.id;
    const rowOrder =
      droppedRow.original.type === BUILDER_ROW_TYPES.TILE && droppedRow.index
        ? Number(droppedRow.index) + 1
        : 0;

    const res = await addMediaPlanRow({
      type: BUILDER_ROW_TYPES.TILE,
      parent,
      order: rowOrder,
    });

    if ('data' in res) {
      const { data } = res;
      if (droppedRow.original.type !== BUILDER_ROW_TYPES.TILE) {
        toggleRowExpanded?.(droppedRow.id.toString(), true);
      }

      setRowState?.([res.data.id], { isNewAdded: true });
      onSuccess(data.name || '');
    }
  };

  const handleDrop = async ({
    item,
    row: droppedRow,
    type = REORDER,
  }: {
    item: DraggableItemType;
    row: BuilderTableRow;
    type?: string;
  }) => {
    const { order = 0 } = droppedRow.original;

    switch (type) {
      case REORDER:
        handleReorder(item, order);
        break;

      case BUILDER_ROW_TYPES.TILE:
        await handleTile(item, droppedRow);
        break;

      default:
        return null;
    }
  };

  return (
    <TableRow
      style={style}
      row={row}
      onDrop={handleDrop}
      setRowState={setRowState}
      isDisabled={isArchivedVersion}
      isParentHighlighted={isParentHighlighted}
      startMeasurePerformance={startMeasurePerformance}
    />
  );
};

export default RenderRow;
